import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, Pin, Globe, Lock, Sparkles, Shield, Upload, ArrowRight, Plus, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import NewDashboardDialog from "@/components/NewDashboardDialog";

export default function DashboardsList() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [dashes, setDashes] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const isAdmin = user?.role === "company_admin" || user?.role === "platform_admin";

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "mine" ? "?mine=true" : filter === "published" ? "?published=true" : "";
      const [d1, d2] = await Promise.all([
        api.get(`/dashboards${params}`),
        api.get("/datasets"),
      ]);
      setDashes(d1.data.dashboards || []);
      setDatasets(d2.data.datasets || []);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const loadSample = async () => {
    try {
      const { data } = await api.post("/datasets/load-sample");
      toast.success(`${data.created.length} sample datasets loaded`);
      // auto-build a Workforce 360 dashboard right away
      try {
        const { data: dash } = await api.post("/dashboards/auto-from-datasets", {
          title: "Workforce 360",
          dataset_ids: data.created.map(d => d.id),
        });
        toast.success("Workforce 360 dashboard ready");
        nav(`/t/${slug}/dashboards/${dash.id}`);
      } catch {}
      await load();
    } catch { toast.error("Failed"); }
  };

  const onCreated = (dash) => { setShowNew(false); nav(`/t/${slug}/dashboards/${dash.id}`); };

  const noData = datasets.length === 0;
  const noDash = dashes.length === 0;

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-caps mb-2">Analytics</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900">Dashboards</h1>
          <p className="text-slate-500 mt-3 max-w-lg">Combine data from any HR system into one interactive dashboard — no BI tool required.</p>
        </div>
        {!noData && isAdmin && (
          <button data-testid="new-dashboard" onClick={() => setShowNew(true)} className="btn-primary"><Plus size={14} /> New dashboard</button>
        )}
      </div>

      {loading ? <div className="text-slate-400">Loading…</div>
      : noData ? (
        <div className="card fade-up">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="chip chip-primary mb-3"><Upload size={12} /> Step 1</div>
              <h2 className="font-display text-2xl font-medium text-slate-900 mb-2">Bring your HR data in first</h2>
              <p className="text-slate-500 mb-6">Upload one or many files from any HR system, then PeopleIQ auto-generates a dashboard. You can then add filters, switch chart types, and add widgets.</p>
              <div className="flex flex-wrap gap-2">
                <Link to={`/t/${slug}/upload`} data-testid="empty-upload" className="btn-primary"><Upload size={14} /> Upload data <ArrowRight size={14} /></Link>
                {isAdmin && <button onClick={loadSample} data-testid="empty-sample" className="btn-ghost"><Sparkles size={14} /> Try sample HR data</button>}
              </div>
            </div>
            <div className="space-y-2">
              {[
                { t: "Upload", s: "CSV / Excel from Workday, SAP, BambooHR, etc." },
                { t: "Auto-generate", s: "PeopleIQ builds the right widgets for you" },
                { t: "Customize", s: "Add filters, switch chart types, add tables" },
                { t: "Publish", s: "Share with your team or make public" },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                  <div className="w-6 h-6 rounded-full bg-white border border-[var(--border)] flex items-center justify-center text-xs font-medium text-slate-700">{i + 1}</div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{s.t}</div>
                    <div className="text-xs text-slate-500">{s.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : noDash ? (
        <div className="card fade-up">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="chip chip-primary mb-3"><Sparkles size={12} /> Step 2</div>
              <h2 className="font-display text-2xl font-medium text-slate-900 mb-2">Create your first dashboard</h2>
              <p className="text-slate-500 mb-6">You have <span className="font-medium text-slate-900">{datasets.length} dataset{datasets.length === 1 ? "" : "s"}</span> ready. Pick one or more and PeopleIQ will auto-generate the right widgets. You'll be able to customize everything.</p>
              {isAdmin && <button onClick={() => setShowNew(true)} data-testid="empty-new-dash" className="btn-primary"><Sparkles size={14} /> Auto-generate dashboard</button>}
            </div>
            <div>
              <div className="label-caps mb-3">Your data sources</div>
              <div className="space-y-2 max-h-64 overflow-y-auto scroll-thin">
                {datasets.map(d => (
                  <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border)]">
                    <FileSpreadsheet size={14} className="text-[var(--primary)]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{d.name}</div>
                      <div className="text-xs text-slate-500 mono">{d.row_count} rows · {d.kind || d.category}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-6">
            {["all","mine","published"].map((k) => (
              <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
                className={`chip ${filter === k ? "chip-primary" : ""} capitalize`}>{k}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashes.map((d, idx) => (
              <Link key={d.id} to={`/t/${slug}/dashboards/${d.id}`} className="card fade-up block" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`dashboard-card-${d.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--primary-soft)] flex items-center justify-center">
                    {d.auto_generated ? <Sparkles size={16} className="text-[var(--primary)]" /> : <Layout size={16} className="text-[var(--primary)]" />}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {d.pinned && <Pin size={12} className="text-amber-500 fill-amber-500" />}
                    {d.is_public ? <Globe size={12} className="text-emerald-600" /> : d.published ? <Globe size={12} className="text-slate-400" /> : <Lock size={12} className="text-slate-300" />}
                    {d.access_control?.enabled && <Shield size={12} className="text-[var(--primary)]" />}
                  </div>
                </div>
                <h3 className="font-display text-lg font-medium text-slate-900 truncate">{d.title}</h3>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2 min-h-[2.5rem]">{d.description || d.dataset_name}</p>
                <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-slate-100">
                  <div><div className="label-caps">Widgets</div><div className="mono text-base mt-1">{d.widget_count}</div></div>
                  <div><div className="label-caps">Owner</div><div className="text-sm mt-1 truncate">{d.owner_name}</div></div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {showNew && <NewDashboardDialog datasets={datasets} onClose={() => setShowNew(false)} onCreated={onCreated} />}
    </div>
  );
}
